import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { contactPageSchema, breadcrumbSchema } from '../utils/seoSchemas';
import { trackContactForm, trackConsultation } from '../utils/analytics';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '971501219341';

const Contact = () => {
  usePageSeo({
    title: 'Contact WPAA | Book a Free Consultation in UAE',
    description: 'Contact White Paper Accounts Auditing in Sharjah, UAE. Book a free consultation, call +971 50 121 9341, office +971 6 744 7940, or email info@wpaauae.com.',
    path: '/contact'
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [consultationData, setConsultationData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    service: ''
  });

  const [notifyWhatsAppContact, setNotifyWhatsAppContact] = useState(false);
  const [notifyWhatsAppConsultation, setNotifyWhatsAppConsultation] = useState(false);

  const handleInputChange = (e, form) => {
    const { name, value } = e.target;
    if (form === 'contact') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setConsultationData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleServiceChange = (value, form) => {
    if (form === 'contact') {
      setFormData(prev => ({ ...prev, service: value }));
    } else {
      setConsultationData(prev => ({ ...prev, service: value }));
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("Message Sent!", {
          description: response.data.message,
        });

        // Google Ads conversion + GA4 + Enhanced Conversions (hashed PII)
        trackContactForm({
          email: formData.email,
          phone: formData.phone,
          firstName: formData.name?.split(' ')[0],
          lastName: formData.name?.split(' ').slice(1).join(' ')
        });

        // Open WhatsApp with pre-filled message only if checkbox is checked
        if (notifyWhatsAppContact) {
          const whatsappMessage = `*New Contact Form Submission*%0A%0A` +
            `*Name:* ${formData.name}%0A` +
            `*Email:* ${formData.email}%0A` +
            `*Phone:* ${formData.phone || 'Not provided'}%0A` +
            `*Company:* ${formData.company || 'Not provided'}%0A` +
            `*Service:* ${formData.service || 'Not specified'}%0A%0A` +
            `*Message:*%0A${formData.message}`;
          
          window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`, '_blank');
        }
        
        setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
        setNotifyWhatsAppContact(false);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Contact form error:', error);
      }
      toast.error("Error sending message", {
        description: "Please try again or contact us directly via email.",
      });
    }
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/consultation`, consultationData);
      if (response.data.success) {
        toast.success("Consultation Booked!", {
          description: response.data.message,
        });

        // Google Ads conversion + GA4 + Enhanced Conversions (hashed PII)
        trackConsultation({
          email: consultationData.email,
          phone: consultationData.phone,
          firstName: consultationData.name?.split(' ')[0],
          lastName: consultationData.name?.split(' ').slice(1).join(' ')
        });

        // Open WhatsApp with pre-filled message only if checkbox is checked
        if (notifyWhatsAppConsultation) {
          const whatsappMessage = `*New Consultation Booking*%0A%0A` +
            `*Name:* ${consultationData.name}%0A` +
            `*Email:* ${consultationData.email}%0A` +
            `*Phone:* ${consultationData.phone}%0A` +
            `*Service:* ${consultationData.service}%0A` +
            `*Preferred Date:* ${consultationData.preferredDate}%0A` +
            `*Preferred Time:* ${consultationData.preferredTime}`;
          
          window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`, '_blank');
        }
        
        setConsultationData({ name: '', email: '', phone: '', preferredDate: '', preferredTime: '', service: '' });
        setNotifyWhatsAppConsultation(false);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Consultation booking error:', error);
      }
      toast.error("Error booking consultation", {
        description: "Please try again or call us directly.",
      });
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: ['+971 50 121 9341', 'Office: +971 6 744 7940', 'WhatsApp Available'],
      link: 'tel:+971501219341',
      showButtons: true
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['info@wpaauae.com', 'Quick response guaranteed'],
      link: 'mailto:info@wpaauae.com'
    },
    {
      icon: MapPin,
      title: 'Location',
      details: ['109, Midaffa Bldg., Musab Bin Omair St.', 'Al Mahatta, Sharjah, UAE'],
      link: null
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Mon - Fri: 9:00 AM - 6:00 PM', 'Sat: 10:00 AM - 2:00 PM'],
      link: null
    }
  ];

  const services = [
    'Audit & Assurance',
    'Tax Consultancy',
    'Accounting Services',
    'Business Advisory',
    'Company Formation',
    'Valuation',
    'goAML Compliance',
    'E-Invoicing Readiness',
    'Free Zone Compliance Services',
    'SME Business Package - Starter',
    'SME Business Package - Growth',
    'SME Business Package - Premium',
    'Other'
  ];

  // Map of URL slugs -> dropdown values for pre-fill
  const servicePrefillMap = {
    'audit': 'Audit & Assurance',
    'tax': 'Tax Consultancy',
    'corporate-tax': 'Tax Consultancy',
    'vat': 'Tax Consultancy',
    'accounting': 'Accounting Services',
    'advisory': 'Business Advisory',
    'business-advisory': 'Business Advisory',
    'formation': 'Company Formation',
    'company-formation': 'Company Formation',
    'valuation': 'Valuation',
    'goaml': 'goAML Compliance',
    'e-invoicing': 'E-Invoicing Readiness',
    'free-zone-services': 'Free Zone Compliance Services',
    'hamriyah-free-zone': 'Free Zone Compliance Services',
    'saif-zone': 'Free Zone Compliance Services',
    'sme-starter': 'SME Business Package - Starter',
    'sme-growth': 'SME Business Package - Growth',
    'sme-premium': 'SME Business Package - Premium'
  };

  const location = useLocation();
  const contactFormRef = useRef(null);
  const consultationFormRef = useRef(null);

  // Pre-fill service from URL query (e.g., /contact?service=sme-growth)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const slug = params.get('service');
    if (!slug) return;
    const mapped = servicePrefillMap[slug];
    if (!mapped) return;

    setFormData(prev => ({ ...prev, service: mapped }));
    setConsultationData(prev => ({ ...prev, service: mapped }));
    toast.success(`${mapped} pre-selected`, {
      description: 'Just fill in your details and we will get back to you.'
    });
    // Smooth-scroll to contact form
    setTimeout(() => {
      const target = params.get('form') === 'consultation' ? consultationFormRef : contactFormRef;
      target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div className="min-h-screen">
      <JsonLd id="ld-contact-page" data={contactPageSchema()} />
      <JsonLd
        id="ld-contact-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Contact', path: '/contact' }
        ])}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-teal-50 via-white to-gray-50">
        <div className="container mx-auto text-center">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
          <h1 className="text-5xl font-bold text-gray-900 mt-4 mb-6">
            Let's Start a Conversation
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Have questions about our services? Ready to schedule a consultation? We're here to help your business succeed.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg group">
                  <CardContent className="p-6 text-center">
                    <div className="bg-teal-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-teal-600 transition-colors">
                      <Icon className="text-teal-600 group-hover:text-white transition-colors" size={24} />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{info.title}</h3>
                    {info.showButtons ? (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-600">{info.details[0]}</p>
                        <div className="flex flex-col gap-2 mt-3">
                          <a 
                            href="tel:+971501219341" 
                            className="inline-flex items-center justify-center px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors"
                            data-testid="call-now-btn"
                          >
                            <Phone size={16} className="mr-2" />
                            Call Now
                          </a>
                          <a 
                            href="https://wa.me/971501219341" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                            data-testid="whatsapp-chat-btn"
                          >
                            <MessageCircle size={16} className="mr-2" />
                            WhatsApp Chat
                          </a>
                        </div>
                      </div>
                    ) : info.link ? (
                      <a href={info.link} className="block">
                        <p className="text-sm text-gray-600 hover:text-teal-600 transition-colors">{info.details[0]}</p>
                        <p className="text-xs text-gray-500 mt-1">{info.details[1]}</p>
                      </a>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600">{info.details[0]}</p>
                        <p className="text-xs text-gray-500 mt-1">{info.details[1]}</p>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <div className="flex items-center space-x-3 mb-3">
                  <MessageSquare className="text-teal-600" size={28} />
                  <h2 className="text-3xl font-bold text-gray-900">Send Us a Message</h2>
                </div>
                <p className="text-gray-600">Fill out the form below and we'll get back to you within 24 hours.</p>
              </div>

              <form ref={contactFormRef} onSubmit={handleContactSubmit} className="bg-white p-8 rounded-xl shadow-lg space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange(e, 'contact')}
                      placeholder="John Doe"
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange(e, 'contact')}
                      placeholder="john@company.com"
                      required
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange(e, 'contact')}
                      placeholder="+971 50 121 9341"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={(e) => handleInputChange(e, 'contact')}
                      placeholder="Your Company"
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Interested In</label>
                  <Select key={`contact-${formData.service}`} value={formData.service} onValueChange={(value) => handleServiceChange(value, 'contact')}>
                    <SelectTrigger className="w-full" data-testid="contact-service-trigger">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service} value={service}>{service}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange(e, 'contact')}
                    placeholder="Tell us about your needs..."
                    required
                    rows={5}
                    className="w-full"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="whatsapp-contact"
                    checked={notifyWhatsAppContact}
                    onChange={(e) => setNotifyWhatsAppContact(e.target.checked)}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="whatsapp-contact" className="text-sm text-gray-600">
                    Also notify via WhatsApp
                  </label>
                </div>

                <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg">
                  <Send size={20} className="mr-2" />
                  Send Message
                </Button>
              </form>
            </div>

            {/* Consultation Booking Form */}
            <div>
              <div className="mb-8">
                <div className="flex items-center space-x-3 mb-3">
                  <Clock className="text-teal-600" size={28} />
                  <h2 className="text-3xl font-bold text-gray-900">Book Free Consultation</h2>
                </div>
                <p className="text-gray-600">Schedule a complimentary consultation with our expert team.</p>
              </div>

              <form ref={consultationFormRef} onSubmit={handleConsultationSubmit} className="bg-white p-8 rounded-xl shadow-lg space-y-6" data-testid="consultation-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <Input
                    name="name"
                    value={consultationData.name}
                    onChange={(e) => handleInputChange(e, 'consultation')}
                    placeholder="John Doe"
                    required
                    className="w-full"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={consultationData.email}
                      onChange={(e) => handleInputChange(e, 'consultation')}
                      placeholder="john@company.com"
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <Input
                      name="phone"
                      value={consultationData.phone}
                      onChange={(e) => handleInputChange(e, 'consultation')}
                      placeholder="+971 50 121 9341"
                      required
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Area *</label>
                  <Select key={`consultation-${consultationData.service}`} value={consultationData.service} onValueChange={(value) => handleServiceChange(value, 'consultation')} required>
                    <SelectTrigger className="w-full" data-testid="consultation-service-trigger">
                      <SelectValue placeholder="Select service area" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service} value={service}>{service}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date *</label>
                    <Input
                      type="date"
                      name="preferredDate"
                      value={consultationData.preferredDate}
                      onChange={(e) => handleInputChange(e, 'consultation')}
                      required
                      className="w-full"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time *</label>
                    <Select onValueChange={(value) => setConsultationData(prev => ({ ...prev, preferredTime: value }))} required>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="09:00">09:00 AM</SelectItem>
                        <SelectItem value="10:00">10:00 AM</SelectItem>
                        <SelectItem value="11:00">11:00 AM</SelectItem>
                        <SelectItem value="12:00">12:00 PM</SelectItem>
                        <SelectItem value="14:00">02:00 PM</SelectItem>
                        <SelectItem value="15:00">03:00 PM</SelectItem>
                        <SelectItem value="16:00">04:00 PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong className="text-teal-700">Note:</strong> This is a free, no-obligation consultation. 
                    Our team will confirm your appointment within 24 hours.
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="whatsapp-consultation"
                    checked={notifyWhatsAppConsultation}
                    onChange={(e) => setNotifyWhatsAppConsultation(e.target.checked)}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="whatsapp-consultation" className="text-sm text-gray-600">
                    Also notify via WhatsApp
                  </label>
                </div>

                <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg">
                  Book Consultation
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="rounded-xl overflow-hidden shadow-lg">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4436.584118034028!2d55.39016157605786!3d25.35103682545119!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4fd6fd68eec07dc9%3A0x7e3645bc685468fc!2sWhite%20Paper%20Accounts%20Auditing%20LLC!5e1!3m2!1sen!2sae!4v1776860123101!5m2!1sen!2sae" 
              width="100%" 
              height="450" 
              style={{ border: 0, borderRadius: '10px' }}
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="White Paper Accounts Auditing Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
